/**
 * Alarm-feed fixtures — a port of the *derivation*, not a table of fake rows.
 *
 * `GET /ops/v1/alarms` reads the `acs_alarms` collection, and every row is
 * written there at raise time (`acs_service/store.py::_maybe_raise_alarm`);
 * the console's dev fixture therefore keeps the same two things the server
 * keeps: the ROWS (derived from the CloudEvents the fixture holds, through the
 * server's own severity table) and the LIFECYCLE (a per-alarm record of the
 * state, the escalation flag and the assignee — the port of the stored row).
 *
 *  - `SEVERITY_BY_TYPE` is the server's fixed table (onu.los + an OFFLINE
 *    device.online_status → critical, optical.degraded → warning,
 *    reconciliation.mismatch → info). The console must never re-derive a
 *    severity itself, so the same rule lives here only to build fixture data.
 *  - an ONLINE `device.online_status` is a recovery, not an alarm — the exact
 *    extra predicate of `_alarm_of()`.
 *  - `alarm_id` IS the CloudEvents id, so the feed and `GET /integration/v1/events`
 *    name the same fact with the same key (the fixture rows are derived from
 *    `FIXTURE_EVENTS`, so that stays true in dev).
 *  - ordering: newest first by `time`, `alarm_id` descending on ties.
 *  - `acked` is the stored acknowledgement fact (a human acknowledged it),
 *    never DERIVED from `state`: a closure nobody acknowledged reads
 *    `cleared` with `acked: false`.
 *
 * The ack side is the `alarm.ack` audit ledger: one entry per (alarm, client), so
 * a second ack from the same client answers `already_acked: true` and writes
 * nothing. `FIXTURE_ACK_CLIENT` stands in for the `client_id` the façade would
 * take from the verified assertion (W1 sessions are not wired yet).
 */

import type { Alarm, AlarmSeverity, AlarmState, CloudEvent } from '../api/types';
import { eventTypeUri } from '../api/types';
import { FIXTURE_EVENTS } from './records';

/** `ALARM_SEVERITIES` in `acs_service/models.py`. */
export const ALARM_SEVERITY_OPTIONS: readonly AlarmSeverity[] = ['critical', 'warning', 'info'];

/** `SEVERITY_BY_TYPE` in `routers/alarms.py`, keyed by the full type URI. */
export const SEVERITY_BY_TYPE: Readonly<Record<string, AlarmSeverity>> = {
  [eventTypeUri('onu.los')]: 'critical',
  [eventTypeUri('optical.degraded')]: 'warning',
  [eventTypeUri('device.online_status')]: 'critical',
  [eventTypeUri('reconciliation.mismatch')]: 'info',
  // W8 (2026-11-04): the fleet watch's two inform rules — a stale device is degraded
  // monitoring, an inform storm actively harms this service.
  [eventTypeUri('device.stale_inform')]: 'warning',
  [eventTypeUri('device.inform_flood')]: 'critical',
};

/** The kind that needs the extra predicate (offline transitions only). */
export const ONLINE_STATUS_TYPE = eventTypeUri('device.online_status');

/** `SCAN_PAGE_SIZE` — the server's `limit` ceiling (1..200). */
export const ALARM_SCAN_PAGE_SIZE = 200;

/** The `client_id` recorded as `acked_by` by the fixture's ack ledger. */
export const FIXTURE_ACK_CLIENT = 'client:console-fixture';

/**
 * The lifecycle the fixture keeps per alarm id — the port of the server's
 * `acs_alarms` row (N1/N2). The server stores these on the ROW, so the
 * fixture stores them on a row too: a fixture row that carried a lifecycle
 * only in the ack ledger would answer «cleared» for a row the server calls
 * «acked», and the two surfaces would tell the operator different stories.
 */
export interface AlarmLifecycle {
  state: AlarmState;
  /**
   * «was this alarm ACKNOWLEDGED» — the ack door's fact, kept apart from the
   * lifecycle state: an alarm closed straight from `open` was never
   * acknowledged, and the `acked` filter counts acknowledgements.
   */
  acknowledged: boolean;
  escalated: boolean;
  escalated_by?: string | null;
  assignee: string | null;
  assigned_by?: string | null;
  /**
   * Who closed it — the fixture's own copy of the server's `cleared_by`, and
   * the reason a replay (the same person again) and a refusal (somebody
   * else's closure) are two different answers.
   */
  cleared_by?: string | null;
}

/** A row that was raised and nobody has touched yet. */
export const OPEN_ALARM_LIFECYCLE: AlarmLifecycle = {
  state: 'open',
  acknowledged: false,
  escalated: false,
  assignee: null,
  cleared_by: null,
};

/** `_alarm_of()`: one event → one alarm row, or `null` when not alarm-bearing. */
export function alarmFromEvent(
  event: CloudEvent,
  lifecycle: AlarmLifecycle = OPEN_ALARM_LIFECYCLE,
): Alarm | null {
  const severity = SEVERITY_BY_TYPE[event.type];
  if (severity === undefined) return null;
  const data: Record<string, unknown> = { ...event.data };
  if (event.type === ONLINE_STATUS_TYPE && data.online !== false) return null;
  return {
    alarm_id: event.id,
    type: event.type,
    severity,
    acs_device_id: String(data.acs_device_id ?? ''),
    time: event.time,
    data,
    // N3 (G-ALM-04): the dedup facets, birthed exactly as the server's
    // `_new_alarm_row` births them — the row below may be collapsed into an
    // earlier one by `collapseAlarmRows`, which is where the counts move.
    fingerprint: alarmFingerprintOf(event),
    occurrences: 1,
    last_seen_at: event.time,
    // `acked` is the ACKNOWLEDGEMENT fact, exactly as the server derives it
    // — never «the row is no longer open»: a closure nobody acknowledged
    // would otherwise be counted as an acknowledgement.
    acked: lifecycle.acknowledged,
    state: lifecycle.state,
    escalated: lifecycle.escalated,
    assignee: lifecycle.assignee,
  };
}

/**
 * N3 (G-ALM-04) — the dedup rule, ported because this file IS the port of
 * the derivation: `alarm_id` stays the BORN event's id, the same-key events
 * inside the flap window collapse onto that row (occurrences counted,
 * `last_seen_at` moved), and both events stay in the events feed — only the
 * alarm row stops repeating.
 */

/** `FLAP_WINDOW_DEFAULT_SECONDS` in `acs_service/alarm_dedup.py`. */
export const ALARM_FLAP_WINDOW_SECONDS = 900;

/** `_FINGERPRINT_BY_TYPE` in `acs_service/alarm_dedup.py`, by the full type URI. */
export const ALARM_FINGERPRINT_BY_TYPE: Readonly<Record<string, string>> = {
  [eventTypeUri('optical.degraded')]: 'threshold_dbm',
  [eventTypeUri('onu.los')]: 'onu_sn',
};

/** `fingerprint_of()` — the cause-within-the-kind, or the empty string. */
export function alarmFingerprintOf(event: CloudEvent): string {
  const field = ALARM_FINGERPRINT_BY_TYPE[event.type];
  if (field === undefined) return '';
  const data = event.data as Record<string, unknown> | undefined;
  return String((data ?? {})[field] ?? '');
}

/** `within_flap()` — datetimes, not strings; an unreadable stamp fails open. */
export function withinFlap(lastSeenAt: string, eventTime: string): boolean {
  const seen = Date.parse(lastSeenAt);
  const at = Date.parse(eventTime);
  if (Number.isNaN(seen) || Number.isNaN(at)) return false;
  return seen >= at - ALARM_FLAP_WINDOW_SECONDS * 1000;
}

/**
 * The feed the server would answer right now, before filters and paging:
 * one pass over the events, same-key rows inside the flap window collapsed
 * onto the row their FIRST event born (its own lifecycle rides along — a
 * collapsed repeat does not re-open an acked row, it counts on it).
 */
export function collapseAlarmRows(
  events: readonly CloudEvent[],
  lifecycles: ReadonlyMap<string, AlarmLifecycle> = new Map(),
): Alarm[] {
  const rows: Alarm[] = [];
  for (const event of events) {
    const row = alarmFromEvent(event, lifecycles.get(event.id) ?? OPEN_ALARM_LIFECYCLE);
    if (!row) continue;
    const hit = rows.find(
      (candidate) =>
        candidate.acs_device_id === row.acs_device_id &&
        candidate.type === row.type &&
        (candidate.fingerprint ?? '') === row.fingerprint &&
        candidate.state !== 'cleared' &&
        withinFlap(candidate.last_seen_at ?? candidate.time, event.time),
    );
    if (hit) {
      hit.occurrences = (hit.occurrences ?? 1) + 1;
      hit.last_seen_at = event.time;
      continue;
    }
    rows.push(row);
  }
  return sortAlarms(rows);
}

/** The fixture's events, through the same collapse. */
export function deriveAlarmRows(
  lifecycles: ReadonlyMap<string, AlarmLifecycle> = new Map(),
): Alarm[] {
  return collapseAlarmRows(FIXTURE_EVENTS, lifecycles);
}

/** `rows.sort(alarm_id, reverse)` then `rows.sort(time, reverse)` on the server. */
export function sortAlarms(rows: readonly Alarm[]): Alarm[] {
  return [...rows].sort(
    (a, b) => b.time.localeCompare(a.time) || b.alarm_id.localeCompare(a.alarm_id),
  );
}

/** An outbox event that is not alarm-bearing (`alarm_id`, `type`) — for the 404 text. */
export function outboxEventOf(alarmId: string): CloudEvent | undefined {
  return FIXTURE_EVENTS.find((event) => event.id === alarmId);
}

/** The server's own detail texts for the two `alarm-not-found` shapes. */
export function unknownAlarmDetail(alarmId: string): string {
  return `no event ${alarmId} in the outbox`;
}

export function notAnAlarmDetail(alarmId: string, type: string): string {
  return `event ${alarmId} (type '${type}') is not an alarm-bearing event`;
}

/**
 * Which `data` keys matter for one alarm kind (the Persian labels live in the
 * page). A fixed table on purpose: the rest of `data` stays available in the row
 * drill-down, and none of these keys are invented — they are the
 * `schemas/cloudevents/*.json` payloads mirrored in `types.ts`.
 */
export function alarmDataFieldsOf(type: string): readonly string[] {
  switch (type) {
    case eventTypeUri('onu.los'):
      return ['alarm', 'severity', 'pon_port', 'olt_ref', 'occurred_at'];
    case eventTypeUri('optical.degraded'):
      return ['rx_dbm', 'threshold_dbm', 'pon_port', 'sample_count', 'sampled_at'];
    case ONLINE_STATUS_TYPE:
      return ['online', 'last_seen_at'];
    case eventTypeUri('reconciliation.mismatch'):
      return ['kind', 'olt_ref', 'action'];
    // W8 (2026-11-04): the fleet watch's two kinds — the measured fact and the rule that
    // judged it, nothing else.
    case eventTypeUri('device.stale_inform'):
      return ['last_inform_at', 'threshold_hours', 'hours_since'];
    case eventTypeUri('device.inform_flood'):
      return ['window_hours', 'inform_count', 'threshold_per_hour'];
    default:
      return ['pon_port', 'olt_ref'];
  }
}
